import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { type BankAccountWithDetails, bankAccountListBankAccountsOptions } from '@column/api';

import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

const LIST_STALE_TIME_MS = 15_000;

interface PinnedBankAccount {
  bankAccountId: string;
  order?: number;
}

interface UsePinnedBankAccountsReturn {
  pinnedAccounts: BankAccountWithDetails[];
  isPinned: (bankAccountId: string) => boolean;
  pinAccount: (bankAccountId: string, description: string) => Promise<void>;
  unpinAccount: (bankAccountId: string, description: string) => Promise<void>;
  togglePin: (bankAccountId: string, description: string) => Promise<void>;
  reorderAccounts: (newOrder: PinnedBankAccount[]) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

interface UsePinnedBankAccountsOptions {
  skipFetch?: boolean;
}

export const usePinnedBankAccounts = ({
  skipFetch = false,
}: UsePinnedBankAccountsOptions = {}): UsePinnedBankAccountsReturn => {
  const { currentUser } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();

  const pinnedBankAccounts = currentUser?.getPlatformSettings?.pinnedBankAccounts ?? [];

  const sortedPinnedIds = useMemo(
    () => [...pinnedBankAccounts].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((pin) => pin.bankAccountId),
    [pinnedBankAccounts]
  );

  const bankAccountQuery = useQuery({
    ...bankAccountListBankAccountsOptions({
      query: { bank_account_ids: sortedPinnedIds.join(','), limit: 100 },
    }),
    enabled: !skipFetch && sortedPinnedIds.length > 0,
    staleTime: LIST_STALE_TIME_MS,
  });

  const pinnedAccounts = useMemo<BankAccountWithDetails[]>(() => {
    if (!bankAccountQuery.data) return [];
    const byId = new Map<string, BankAccountWithDetails>();
    for (const account of bankAccountQuery.data.bank_accounts) {
      byId.set(account.id, account);
    }
    return sortedPinnedIds
      .map((id) => byId.get(id))
      .filter((account): account is BankAccountWithDetails => account !== undefined);
  }, [bankAccountQuery.data, sortedPinnedIds]);

  const isPinned = useCallback(
    (bankAccountId: string): boolean => pinnedBankAccounts.some((pin) => pin.bankAccountId === bankAccountId),
    [pinnedBankAccounts]
  );

  const updateSettingsMutation = useMutation({
    mutationFn: async (newPinnedAccounts: PinnedBankAccount[]) => {
      if (!currentUser) {
        throw new Error('No current user');
      }
      return currentUser.updatePlatformSettings({
        pinnedBankAccounts: newPinnedAccounts,
      });
    },
  });

  const pinAccount = useCallback(
    async (bankAccountId: string, description: string) => {
      const currentPinned = pinnedBankAccounts;
      const maxOrder = Math.max(...currentPinned.map((pin) => pin.order ?? 0), -1);

      const newPinnedAccount = {
        bankAccountId,
        order: maxOrder + 1,
      };

      try {
        await updateSettingsMutation.mutateAsync([...currentPinned, newPinnedAccount]);
        addSuccessNotification({
          content: `${description} pinned successfully`,
        });
      } catch (err) {
        addDangerNotification({
          content: err instanceof Error ? err.message : 'Failed to pin account',
        });
      }
    },
    [pinnedBankAccounts, updateSettingsMutation, addSuccessNotification, addDangerNotification]
  );

  const unpinAccount = useCallback(
    async (bankAccountId: string, description: string) => {
      const filteredPins = pinnedBankAccounts
        .filter((pin) => pin.bankAccountId !== bankAccountId)
        .map((pin, index) => ({ ...pin, order: index }));

      try {
        await updateSettingsMutation.mutateAsync(filteredPins);
        addSuccessNotification({
          content: `${description} unpinned successfully`,
        });
      } catch (err) {
        addDangerNotification({
          content: err instanceof Error ? err.message : 'Failed to unpin account',
        });
      }
    },
    [pinnedBankAccounts, updateSettingsMutation, addSuccessNotification, addDangerNotification]
  );

  const togglePin = useCallback(
    async (bankAccountId: string, description: string) => {
      if (isPinned(bankAccountId)) {
        await unpinAccount(bankAccountId, description);
      } else {
        await pinAccount(bankAccountId, description);
      }
    },
    [isPinned, pinAccount, unpinAccount]
  );

  const reorderAccounts = useCallback(
    async (newOrder: PinnedBankAccount[]) => {
      try {
        await updateSettingsMutation.mutateAsync(newOrder);
        addSuccessNotification({
          content: 'Pinned accounts reordered successfully',
        });
      } catch (err) {
        addDangerNotification({
          content: err instanceof Error ? err.message : 'Failed to reorder accounts',
        });
      }
    },
    [updateSettingsMutation, addSuccessNotification, addDangerNotification]
  );

  return {
    pinnedAccounts,
    isPinned,
    pinAccount,
    unpinAccount,
    togglePin,
    reorderAccounts,
    isLoading: bankAccountQuery.isLoading,
    error: bankAccountQuery.error instanceof Error ? bankAccountQuery.error : null,
  };
};
